import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useEscalateCaseMutation } from '@/services/api/mattersService';

interface EscalateCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  matterName: string;
  matterStatus?: string;
}

export const EscalateCaseModal: React.FC<EscalateCaseModalProps> = ({
  isOpen,
  onClose,
  matterId,
  matterName,
  matterStatus,
}) => {
  const [description, setDescription] = useState('');
  const [escalateCase, { isLoading }] = useEscalateCaseMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!description.trim()) {
      return;
    }

    try {
      await escalateCase({
        matterId,
        description: description.trim(),
      }).unwrap();

      toast.success('Case escalated to Managing Paralegal');
      setDescription('');
      onClose();
    } catch {
      toast.error('Failed to escalate case', {
        description: 'Please try again or contact support.',
      });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setDescription('');
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey && description.trim() && !isLoading) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader className="pb-1 min-w-0">
          <DialogTitle className="text-sm font-semibold text-slate-900 dark:text-zinc-100">
            Escalate Case
          </DialogTitle>
          <div className="space-y-0.5 pt-1">
            <p className="text-xs font-medium text-slate-700 dark:text-zinc-300 truncate">
              {matterName}
            </p>
            {matterStatus && (
              <p className="text-xs text-slate-500 dark:text-zinc-400">
                Status: {matterStatus}
              </p>
            )}
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-3 min-w-0">
            <div className="space-y-1">
              <Label
                htmlFor="escalation-description"
                className="text-sm font-medium text-slate-700 dark:text-zinc-300"
              >
                Reason
                <span className="text-red-500 ml-0.5">*</span>
              </Label>
              <Textarea
                id="escalation-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Brief explanation..."
                rows={2}
                className="resize-none text-sm"
                disabled={isLoading}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter className="pt-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={!description.trim() || isLoading}
            >
              {isLoading ? 'Escalating...' : 'Escalate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EscalateCaseModal;
